import React, { useState, useEffect } from 'react';
import './Modal.css';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';

const EditOfferAvailabilityModal = ({ isOpen, onClose, data, onSubmit }) => {
    const [formData, setFormData] = useState({
        videoLink: '',
        is_active: false,
        is_popular: false,
        is_featured: false,
        is_mega_offer: false,
        is_carousel: false,
        is_admin_update: false,
        is_nudge_offer: false,
        nudge_priority: 999,
        game_type: 0, // Add game_type array
    });

    // Game type options
    const gameTypeOptions = [
        { value: 0, label: 'Not Selected' },
        { value: 1, label: 'Quick' },
        { value: 2, label: 'Medium' },
        { value: 3, label: 'Guarantee' },
    ];

    useEffect(() => {
        if (data) {
            const selectedGameType = data.game_type !== undefined && data.game_type !== null
                ? gameTypeOptions.find(option => option.value === data.game_type)
                : null;

            setFormData({
                videoLink: data.videoLink || '',
                is_active: data.is_active || false,
                is_popular: data.is_popular || false,
                is_featured: data.is_featured || false,
                is_mega_offer: data.is_mega_offer || false,
                is_carousel: data.is_carousel || false,
                is_admin_update: data.is_admin_update || false,
                is_nudge_offer: data.is_nudge_offer || false,
                nudge_priority: data.nudge_priority || 999,
                game_type: selectedGameType, // Store as option objects
            });
        }
    }, [data]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Handle Autocomplete change
    const handleGameTypeChange = (event, newValue) => {
        setFormData(prev => ({
            ...prev,
            game_type: newValue || null // Store the selected option objects
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Convert game_type option objects back to array of numbers for submission
        const submissionData = {
            ...formData,
            game_type: formData.game_type?.value !== undefined ? formData.game_type.value : null
        };
        onSubmit(submissionData);
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Edit Offer Availability</h3>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        <div className="form-group">
                            <label>Offer Name (Read Only)</label>
                            <input
                                type="text"
                                value={data?.offer_id?.name || ''}
                                disabled
                                className="form-control"
                            />
                        </div>

                        <div className="form-group">
                            <label>Country Code (Read Only)</label>
                            <input
                                type="text"
                                value={data?.countryCode || ''}
                                disabled
                                className="form-control"
                            />
                        </div>

                        <div className="form-group">
                            <label>Video Link</label>
                            <input
                                type="text"
                                name="videoLink"
                                value={formData.videoLink}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="Enter video link"
                            />
                        </div>

                        {/* Game Type Autocomplete */}
                        <div className="form-group">
                            <label>Game Type</label>
                            <Autocomplete
                                id="game-type-autocomplete"
                                options={gameTypeOptions}
                                getOptionLabel={(option) => option.label}
                                value={formData.game_type}
                                onChange={handleGameTypeChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        placeholder="Select game type"
                                        size="small"
                                    />
                                )}
                                isOptionEqualToValue={(option, value) => option.value === value?.value}
                                style={{ width: '100%' }}
                            />
                            <small className="form-text text-muted">
                                Select one or more game types
                            </small>
                        </div>

                        <div className="form-group-checkboxes">
                            <div className="checkbox-item">
                                <input
                                    type="checkbox"
                                    id="is_active"
                                    name="is_active"
                                    checked={formData.is_active}
                                    onChange={handleChange}
                                />
                                <label htmlFor="is_active">Is Active</label>
                            </div>

                            <div className="checkbox-item">
                                <input
                                    type="checkbox"
                                    id="is_carousel"
                                    name="is_carousel"
                                    checked={formData.is_carousel}
                                    onChange={handleChange}
                                />
                                <label htmlFor="is_carousel">Is Carousel</label>
                            </div>

                            <div className="checkbox-item">
                                <input
                                    type="checkbox"
                                    id="is_featured"
                                    name="is_featured"
                                    checked={formData.is_featured}
                                    onChange={handleChange}
                                />
                                <label htmlFor="is_featured">Is Featured</label>
                            </div>

                            <div className="checkbox-item">
                                <input
                                    type="checkbox"
                                    id="is_popular"
                                    name="is_popular"
                                    checked={formData.is_popular}
                                    onChange={handleChange}
                                />
                                <label htmlFor="is_popular">Is Popular</label>
                            </div>

                            <div className="checkbox-item">
                                <input
                                    type="checkbox"
                                    id="is_mega_offer"
                                    name="is_mega_offer"
                                    checked={formData.is_mega_offer}
                                    onChange={handleChange}
                                />
                                <label htmlFor="is_mega_offer">Is Mega Offer</label>
                            </div>

                            <div className="checkbox-item">
                                <input
                                    type="checkbox"
                                    id="is_admin_update"
                                    name="is_admin_update"
                                    checked={formData.is_admin_update}
                                    onChange={handleChange}
                                />
                                <label htmlFor="is_admin_update">Admin Update</label>
                            </div>

                            <div className="checkbox-item">
                                <input
                                    type="checkbox"
                                    id="is_nudge_offer"
                                    name="is_nudge_offer"
                                    checked={formData.is_nudge_offer}
                                    onChange={handleChange}
                                />
                                <label htmlFor="is_nudge_offer">Is Nudge Offer</label>
                            </div>

                            <div className="form-group">
                                <label>Nudge Priority</label>
                                <input
                                    type="number"
                                    name="nudge_priority"
                                    value={formData.nudge_priority}
                                    onChange={handleChange}
                                    className="form-control"
                                    placeholder="Enter nudge priority (999 default)"
                                    min="1"
                                    step="1"
                                />
                                <small className="form-text text-muted">Lower number = higher priority (1 is highest, 999 is lowest)</small>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary">
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditOfferAvailabilityModal;